import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { logEvent } from 'firebase/analytics'
import { isBefore } from 'date-fns'

import { NPSCard } from './NpsCard'

import { fetchNpsAppearanceNextDate, message, sendNps } from '~/services'
import { useLoggedInUser, useMessageCenter } from '~/context'
import { hasObjectValue } from '~/utils'
import { IUserNPSAppearance } from '~/interfaces'
import { analytics } from '~/firebase-analytics'

const NPS_SURVEY_SETTINGS = 'npsSurveySettings'

export const NPSSurvey = (): JSX.Element => {
  const { user } = useLoggedInUser()
  const { throwCustomAlert, throwErrorModal } = useMessageCenter()
  const hasUser = hasObjectValue({ ...user })

  const [isNpsOpen, setIsNpsOpen] = useState<boolean>(false)
  const { data } = useQuery<IUserNPSAppearance, AxiosError>(
    ['nps'],
    () => fetchNpsAppearanceNextDate({ pathParams: { userId: user!.objeto?.[0]?.CodLogin } }),
    { enabled: hasUser }
  )

  useEffect(() => {
    const isLastFeedbackExpired = data ? isBefore(new Date(data?.nextScreenAppearance), new Date()) : false

    const npsSurveySettings = JSON.parse(sessionStorage.getItem(NPS_SURVEY_SETTINGS) ?? '{}')
    const askMeLater = hasObjectValue({ ...npsSurveySettings }) ? npsSurveySettings.askMeLater : false

    setIsNpsOpen(isLastFeedbackExpired && !askMeLater)
  }, [data])

  const handleAskMeLater = () => {
    sessionStorage.setItem(NPS_SURVEY_SETTINGS, JSON.stringify({ askMeLater: true }))
    setIsNpsOpen(false)
  }

  const handleSubmit = async (npsValue: number) => {
    if (user?.objeto?.[0]?.CodLogin && npsValue) {
      try {
        await sendNps({ userId: user.objeto[0].CodLogin, npsResponse: npsValue })
        throwCustomAlert({
          title: message('feedback.feedbackSentTitle'),
          subtitle: message('feedback.feedbackSentMessage'),
          buttonMessage: message('feedback.feedbackSentButton')
        })
        sessionStorage.removeItem(NPS_SURVEY_SETTINGS)
        logEvent(analytics, 'nps_sent')
      } catch (error) {
        throwErrorModal({
          title: message('feedback.feedbackNotSentTitle'),
          subtitle: message('feedback.npsNotSentMessage'),
          buttonMessage: message('feedback.feedbackSentButton')
        })
        logEvent(analytics, 'nps_error')
      }
      setIsNpsOpen(false)
    }
  }

  return isNpsOpen ? (
    ReactDOM.createPortal(<NPSCard onClose={handleAskMeLater} onSubmit={handleSubmit} />, document.body)
  ) : (
    <></>
  )
}
